import React, {useCallback, useEffect, useRef, useState} from 'react'

import {useDispatch, useSelector} from 'react-redux'
import {useLocation, useNavigate} from 'react-router-dom'

import {alertService} from '../../infra/utils/alerts'
import {
  cleanupPostMessage,
  initializePostMessage,
  postMessageToChild
} from '../../infra/utils/postMessageToIFrame'
import {useIframeConfig} from '../../shared/hooks/useIframeConfig'
import {logoutUser} from '../../slices/thunks'

const IFrame = () => {
  const iframeRef = useRef(null)
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const authToken = localStorage.getItem('authToken')
  const [currentLanguage, setCurrentLanguage] = useState(() =>
    JSON.parse(localStorage.getItem('I18N_LANGUAGE') || '{}')
  )
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const infoData = useSelector(state => state.Info)
  const userData = useSelector(state => state.Profile.user)

  const {iframeBaseUrl} = useIframeConfig()

  const handleIFrameLink = useCallback(() => {
    const currentUrl = new URL(window.location.href)
    const queryParams = new URLSearchParams(currentUrl.search)
    const urlParam = queryParams.get('url')
    const baseUrl = urlParam ? `${iframeBaseUrl}/${urlParam}` : iframeBaseUrl

    const url = new URL(baseUrl)
    url.searchParams.set('languageId', currentLanguage?.id?.toString() || '')

    queryParams.forEach((value, key) => {
      if (key !== 'languageId' && key !== 'url') {
        url.searchParams.set(key, value)
      }
    })

    const finalUrl = `${url.toString()}${location.hash}`

    return finalUrl
  }, [location.search, location.hash, currentLanguage])

  const updateIframeSrc = useCallback(() => {
    if (iframeRef.current) {
      iframeRef.current.src = handleIFrameLink()
    }
  }, [handleIFrameLink])

  useEffect(() => {
    const handleLanguageChange = () => {
      const newLanguage = JSON.parse(
        localStorage.getItem('I18N_LANGUAGE') || '{}'
      )
      setCurrentLanguage(newLanguage)
      updateIframeSrc()
    }

    const originalSetItem = localStorage.setItem
    localStorage.setItem = function (key) {
      originalSetItem.apply(this, arguments)
      if (key === 'I18N_LANGUAGE') {
        handleLanguageChange()
      }
    }

    window.addEventListener('storage', e => {
      if (e.key === 'I18N_LANGUAGE') {
        handleLanguageChange()
      }
    })

    return () => {
      localStorage.setItem = originalSetItem
      window.removeEventListener('storage', handleLanguageChange)
    }
  }, [updateIframeSrc])

  const sendMessageToIframe = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      const message = {
        type: 'authentication',
        token: authToken,
        isHeaderVisible: 'false'
      }
      iframeRef.current.contentWindow.postMessage(
        message,
        window.env.REACT_APP_IFRAME_PARENT_URL
      )
    }
  }, [authToken])

  useEffect(() => {
    if (iframeLoaded) {
      sendMessageToIframe()
    }
  }, [iframeLoaded, sendMessageToIframe])

  useEffect(() => {
    initializePostMessage(iframeRef)

    return () => {
      cleanupPostMessage()
    }
  }, [])

  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true)
    const frame = document.getElementById('iframe')
    if (frame) {
      frame.style.height = 'calc(100vh - 77px)'
      frame.style.width = '100%'
    }
  }, [])

  const handleAlertMessage = alertData => {
    alertService[alertData.alertType]({
      heading: alertData.heading,
      message: alertData.message
    })
  }

  const generateNewUrl = eventData => {
    const newUrl = eventData.path
    const newHash = eventData.hash
    const completeUrl = `iframe?languageId=${currentLanguage?.id}&url=${newUrl}${newHash}`

    return completeUrl
  }

  useEffect(() => {
    const handleMessage = event => {
      if (event?.data?.message === 'logout') {
        dispatch(logoutUser())
        navigate('/logout')
      }

      if (event?.data?.message === 'inLogin') {
        dispatch(logoutUser())
        navigate('/login')
      }

      if (event?.data?.type === 'alert') {
        handleAlertMessage(event.data)
      }

      if (event?.data?.type === 'navigation') {
        window.history.replaceState(null, '', generateNewUrl(event.data.data))
      }

      if (event?.data?.type === 'requestInfoData') {
        sendMessageToIframe()

        if (!infoData.isLoading && infoData.data.ready) {
          postMessageToChild('infoData', infoData.data)
        }

        if (userData) {
          postMessageToChild('aspnetUserInfo', userData)
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [navigate, infoData, generateNewUrl])

  return (
    <div
      style={{
        height: 'calc(100vh - 77px)',
        width: '100%',
        marginTop: 77,
        overflow: 'hidden'
      }}>
      <iframe
        id="iframe"
        style={{
          margin: 0,
          padding: 0,
          height: '100%',
          width: '100%',
          border: 'none'
        }}
        ref={iframeRef}
        onLoad={handleIframeLoad}
        src={handleIFrameLink()}
        title="Content iframe"
      />
    </div>
  )
}

export default IFrame
